// =====================================================
// COMPOSANT: FluxCard — carte « Entrées de la semaine / du mois »
// =====================================================
// Deux métriques d'entrée (BPI entrés, PI convertis), chacune avec valeur +
// Δ N-1. Design system crm-4l (Card + zinc). Le delta N-1 n'apparaît que si
// `showN1`.

import type { LucideIcon } from 'lucide-react';
import { BadgeEuro, UserPlus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { DeltaValue, FluxCardData } from '@/lib/dashboard-stats';
import { DeltaPill } from './DeltaPill';

function fmt(n: number): string {
  return n.toLocaleString('fr-FR');
}

function MetricRow({
  icon: Icon,
  label,
  metric,
  showN1,
}: {
  icon: LucideIcon;
  label: string;
  metric: DeltaValue;
  showN1: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-zinc-500">
        <Icon className="h-4 w-4" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-2xl font-bold tabular-nums text-zinc-950">{fmt(metric.value)}</span>
        {showN1 ? <DeltaPill delta={metric} /> : null}
      </div>
    </div>
  );
}

interface FluxCardProps {
  title: string;
  subtitle: string;
  card: FluxCardData;
  showN1: boolean;
}

export function FluxCard({ title, subtitle, card, showN1 }: FluxCardProps) {
  return (
    <Card className="border border-zinc-200">
      <CardContent className="flex flex-col gap-4 px-6">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-zinc-950">{title}</h3>
          <span className="text-xs font-medium uppercase tracking-wide text-zinc-400">
            {subtitle}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <MetricRow icon={BadgeEuro} label="BPI entrés" metric={card.bpiEntres} showN1={showN1} />
          <MetricRow
            icon={UserPlus}
            label="PI convertis"
            metric={card.piConvertis}
            showN1={showN1}
          />
        </div>
      </CardContent>
    </Card>
  );
}
